import { useState, useEffect, useCallback } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Chip,
  CircularProgress,
  Button,
  TextField,
  InputAdornment,
  alpha,
} from '@mui/material'
import {
  IconNews,
  IconCalendar,
  IconArrowRight,
  IconInbox,
  IconSearch,
  IconWorld,
} from '@tabler/icons-react'
import { tokens } from '@/theme'
import { apiUrl } from '@/lib/apiBase'

// Industry-news roundups (category='industry-news', type='digest') are the
// *external* curated stream — visually distinct from the blue GSA articles and
// the green decision digests. A muted gold accent keeps the two datasets from
// blurring (per WEEKEND-NEWS-SCOUT-PLAN "two datasets stay separate").
const INDUSTRY_NEWS_ACCENT = '#C8A97E'
const INDUSTRY_NEWS_CATEGORY = 'industry-news'

// ─── Types — articles are publications of type "article" (epic e9) ─────────────
// The public News page is a single, canonical surface backed by the publication
// API. Digests (former newsletters) are an admin/email concept and are not
// listed here — we only request type=article.

interface Publication {
  publication_id: number
  type: 'article' | 'digest'
  title: string
  summary: string | null
  status: string
  category: string | null
  featured: boolean
  read_min: number | null
  source_name: string | null
  gsa_id: number | null
  published_at: string | null
  created_at: string | null
}

interface PublicationPage {
  items: Publication[]
  total: number
}

// Bundled static dataset shape (public/data/news.json) — used as an offline
// fallback when the live API isn't reachable (e.g. static CloudFront hosting).
// Regenerate from the live DB so ids stay canonical and the snapshot is fresh:
//   psql … -c "SELECT json_build_object('generated_at', …, 'items', json_agg(
//     json_build_object('publication_id', publication_id, 'id', publication_id, …)))
//     FROM publication WHERE type='article' AND status='published'"
//   > src/web/public/data/news.json
interface StaticNewsItem {
  // Canonical publication id (drives /news/:id). `id` is kept as a legacy alias
  // for older bundles; prefer publication_id.
  publication_id?: number
  id: number
  title: string
  summary: string | null
  category: string | null
  published_at: string | null
  featured: boolean
  read_min: number | null
}

interface StaticNewsBundle {
  generated_at?: string
  items: StaticNewsItem[]
}

// Offline fallback older than this is flagged as potentially stale in the UI.
const FALLBACK_STALE_AFTER_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null | undefined): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

function staticToPublication(n: StaticNewsItem): Publication {
  return {
    // Prefer the canonical publication id; fall back to the legacy `id` only
    // for older bundles. Using the legacy id here was the cause of /news/:id
    // 404s / wrong-article links from the offline fallback (T-143).
    publication_id: n.publication_id ?? n.id,
    type: 'article',
    title: n.title,
    summary: n.summary,
    status: 'published',
    category: n.category,
    featured: n.featured,
    read_min: n.read_min,
    source_name: null,
    gsa_id: null,
    published_at: n.published_at,
    created_at: n.published_at,
  }
}

// ─── Article card — shares the canonical /news/:id URL + publication_id ─────────

function ArticleCard({ item }: { item: Publication }) {
  const accent = tokens.blue.main
  const publishedDate = item.published_at ?? item.created_at

  return (
    <Card
      sx={{
        borderTop: `3px solid ${accent}`,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/news/${item.publication_id}`}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Category badge */}
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              icon={<IconNews size={12} />}
              label={item.category ?? 'Update'}
              size="small"
              sx={{
                background: alpha(accent, 0.08),
                color: accent,
                border: `1px solid ${alpha(accent, 0.2)}`,
                fontFamily: '"DM Mono", monospace',
                fontSize: '0.65rem',
                letterSpacing: '0.04em',
                '& .MuiChip-icon': { color: 'inherit', ml: '6px' },
              }}
            />
          </Stack>

          {/* Title */}
          <Typography
            variant="h6"
            sx={{

              fontSize: '1.1rem',
              fontWeight: 600,
              lineHeight: 1.35,
              mb: 1.25,
              color: tokens.textDark,
            }}
          >
            {item.title}
          </Typography>

          {/* Summary */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
              flex: 1,
              fontFamily: '"Syne", system-ui, sans-serif',
              lineHeight: 1.6,
            }}
          >
            {item.summary ?? ''}
          </Typography>

          {/* Footer */}
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
              <IconCalendar size={13} />
              <Typography variant="caption" sx={{ fontFamily: '"DM Mono", monospace', letterSpacing: '0.03em' }}>
                {formatDate(publishedDate)}
                {item.read_min ? ` · ${item.read_min} min` : ''}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: accent }}>
              <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: '"Syne", system-ui, sans-serif' }}>
                Read
              </Typography>
              <IconArrowRight size={14} />
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

// ─── Industry-news card — distinct gold roundup, links to the same /news/:id ────

function IndustryNewsCard({ item }: { item: Publication }) {
  const accent = INDUSTRY_NEWS_ACCENT
  const publishedDate = item.published_at ?? item.created_at

  return (
    <Card
      sx={{
        borderTop: `3px solid ${accent}`,
        background: alpha(accent, 0.04),
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/news/${item.publication_id}`}
        sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              icon={<IconWorld size={12} />}
              label="Industry News"
              size="small"
              sx={{
                background: alpha(accent, 0.12),
                color: '#7A5C2E',
                border: `1px solid ${alpha(accent, 0.4)}`,
                fontFamily: '"DM Mono", monospace',
                fontSize: '0.65rem',
                letterSpacing: '0.04em',
                '& .MuiChip-icon': { color: 'inherit', ml: '6px' },
              }}
            />
          </Stack>

          <Typography
            variant="h6"
            sx={{ fontSize: '1.1rem', fontWeight: 600, lineHeight: 1.35, mb: 1.25, color: tokens.textDark }}
          >
            {item.title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2,
              flex: 1,
              fontFamily: '"Syne", system-ui, sans-serif',
              lineHeight: 1.6,
            }}
          >
            {item.summary ?? ''}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: 'text.secondary' }}>
              <IconCalendar size={13} />
              <Typography variant="caption" sx={{ fontFamily: '"DM Mono", monospace', letterSpacing: '0.03em' }}>
                {formatDate(publishedDate)}
                {item.read_min ? ` · ${item.read_min} min` : ''}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', color: '#7A5C2E' }}>
              <Typography variant="caption" sx={{ fontWeight: 700, fontFamily: '"Syne", system-ui, sans-serif' }}>
                Read
              </Typography>
              <IconArrowRight size={14} />
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 12

export default function News() {
  const [items, setItems] = useState<Publication[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  // Industry-news roundups (the external curated stream) are fetched separately
  // — they are type='digest' so the article list above never includes them, and
  // they render in their own visually-distinct band (T-189).
  const [industryNews, setIndustryNews] = useState<Publication[]>([])
  // Set when we're showing the bundled offline snapshot and it's older than
  // FALLBACK_STALE_AFTER_MS — surfaced as a small notice so visitors know the
  // list may not reflect the latest published articles.
  const [staleFallback, setStaleFallback] = useState(false)

  // The public list endpoint uses page-based pagination (pagination_params:
  // page + page_size); sending `offset` was a no-op, so Load More re-fetched
  // page 1 and appended duplicates. Request explicit 1-based pages instead.
  const fetchPage = useCallback((pageNum: number) => {
    setLoading(true)
    setError(null)
    const params = new URLSearchParams()
    params.set('type', 'article')
    params.set('page', String(pageNum))
    params.set('page_size', String(PAGE_SIZE))
    params.set('sort', 'published_at')
    params.set('order', 'desc')

    fetch(apiUrl(`/api/agro/v1/publications?${params.toString()}`))
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json() as Promise<PublicationPage>
      })
      .then(data => {
        // Replace on the first page, append on later ones — and dedupe by
        // publication_id so a re-fetched page can never inject duplicates.
        setItems(prev => {
          if (pageNum === 1) return data.items
          const seen = new Set(prev.map(i => i.publication_id))
          return [...prev, ...data.items.filter(i => !seen.has(i.publication_id))]
        })
        setTotal(data.total)
        setPage(pageNum)
        setStaleFallback(false) // live data — not the offline snapshot
      })
      .catch(() => {
        // Offline / static-hosting fallback: bundled dataset (first page only).
        if (pageNum !== 1) {
          setError('Failed to load more articles. Please try again later.')
          return
        }
        return fetch(`${import.meta.env.BASE_URL}data/news.json`)
          .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
          .then((data: StaticNewsBundle) => {
            const mapped = data.items.map(staticToPublication)
            setItems(mapped)
            setTotal(mapped.length)
            setPage(1)
            // Freshness guard: flag a clearly-old offline snapshot.
            const gen = data.generated_at ? Date.parse(data.generated_at) : NaN
            setStaleFallback(!Number.isNaN(gen) && Date.now() - gen > FALLBACK_STALE_AFTER_MS)
          })
          .catch(() => setError('Failed to load articles. Please try again later.'))
      })
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    setItems([])
    setPage(1)
    fetchPage(1)
  }, [fetchPage])

  // Industry-news roundups: published digests in the dedicated category. Best
  // effort — if the API is unreachable (static hosting / offline) we simply omit
  // the band rather than failing the whole page.
  useEffect(() => {
    const controller = new AbortController()
    const params = new URLSearchParams()
    params.set('type', 'digest')
    params.set('category', INDUSTRY_NEWS_CATEGORY)
    params.set('page', '1')
    params.set('page_size', '6')
    params.set('sort', 'published_at')
    params.set('order', 'desc')

    fetch(apiUrl(`/api/agro/v1/publications?${params.toString()}`), { signal: controller.signal })
      .then(res => (res.ok ? (res.json() as Promise<PublicationPage>) : Promise.reject(res.status)))
      .then(data => setIndustryNews(data.items ?? []))
      .catch(() => {
        /* offline / static hosting — omit the industry-news band */
      })

    return () => controller.abort()
  }, [])

  const filtered = items.filter(a => {
    const q = search.toLowerCase()
    return (
      a.title.toLowerCase().includes(q) ||
      (a.summary ?? '').toLowerCase().includes(q)
    )
  })

  const filteredIndustry = industryNews.filter(a => {
    const q = search.toLowerCase()
    return (
      a.title.toLowerCase().includes(q) ||
      (a.summary ?? '').toLowerCase().includes(q)
    )
  })

  // "Load more" only makes sense when not filtering client-side.
  const hasMore = !search && items.length < total

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: tokens.green.dark,
          color: '#fff',
          pt: { xs: 8, md: 11 },
          pb: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ color: alpha('#fff', 0.5), display: 'block', mb: 1 }}>
            AgroLense Updates
          </Typography>
          <Typography
            variant="h1"
            sx={{
              color: '#fff',

              fontSize: { xs: '2.2rem', md: '3.4rem' },
              fontWeight: 700,
              mb: 2,
            }}
          >
            News &amp; Updates
          </Typography>
          <Typography
            variant="body1"
            sx={{ opacity: 0.8, maxWidth: 540, mx: 'auto', fontFamily: '"Syne", system-ui, sans-serif' }}
          >
            Aggregated updates on groundwater, GSA activity, irrigation, and agricultural data —
            organized so you don't have to dig.
          </Typography>
        </Container>
      </Box>

      {/* ── Content ──────────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 } }}>
        {/* Search */}
        <Stack direction="row" sx={{ mb: 4 }}>
          <TextField
            placeholder="Search articles…"
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <IconSearch size={16} style={{ color: tokens.green.main }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: { xs: '100%', sm: 320 } }}
          />
        </Stack>

        {/* Offline-snapshot freshness notice (T-143) */}
        {staleFallback && (
          <Box
            sx={{
              mb: 4, px: 2, py: 1.5, borderRadius: 2,
              bgcolor: alpha(tokens.blue.main, 0.08),
              border: `1px solid ${alpha(tokens.blue.main, 0.25)}`,
              display: 'flex', alignItems: 'center', gap: 1,
            }}
          >
            <IconInbox size={18} style={{ color: tokens.blue.main, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You're viewing a saved offline copy that may be out of date — reconnect to see the latest articles.
            </Typography>
          </Box>
        )}

        {/* ── Industry News band — distinct external curated roundups (T-189) ── */}
        {filteredIndustry.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <IconWorld size={18} style={{ color: INDUSTRY_NEWS_ACCENT }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: tokens.textDark }}>
                Industry News
              </Typography>
            </Stack>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', mb: 3, maxWidth: 620, fontFamily: '"Syne", system-ui, sans-serif' }}
            >
              Curated from public sources and kept separate from our tracked GSA decision data.
              Each roundup links to the original publishers; inclusion is not endorsement.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
                gap: 3,
              }}
            >
              {filteredIndustry.map(item => (
                <IndustryNewsCard key={item.publication_id} item={item} />
              ))}
            </Box>
          </Box>
        )}

        {/* Loading */}
        {loading && items.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
            <CircularProgress size={36} sx={{ color: tokens.green.main }} />
          </Box>
        )}

        {/* Error */}
        {error && (
          <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
            <IconInbox size={40} style={{ color: tokens.textMuted, marginBottom: 12 }} />
            <Typography variant="body1" sx={{ mb: 2 }}>{error}</Typography>
            <Button variant="outlined" color="primary" size="small" onClick={() => fetchPage(1)}>
              Try again
            </Button>
          </Box>
        )}

        {/* Empty — only when neither articles nor industry-news roundups exist */}
        {!loading && !error && items.length === 0 && industryNews.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 12 }}>
            <IconInbox size={48} style={{ color: tokens.textMuted, marginBottom: 16 }} />
            <Typography variant="h5" sx={{   mb: 1, color: tokens.textDark }}>
              Nothing published yet
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 380, mx: 'auto' }}>
              Published articles will appear here. Check back soon.
            </Typography>
          </Box>
        )}

        {/* Grid */}
        {items.length > 0 && (
          <>
            <Typography variant="overline" sx={{ color: 'text.secondary', display: 'block', mb: 3 }}>
              {search
                ? `${filtered.length} ${filtered.length === 1 ? 'result' : 'results'}`
                : `${total} ${total === 1 ? 'article' : 'articles'}`}
            </Typography>

            {filtered.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                <Typography>No articles match your search.</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
                  gap: 3,
                }}
              >
                {filtered.map(item => (
                  <ArticleCard key={item.publication_id} item={item} />
                ))}
              </Box>
            )}

            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => fetchPage(page + 1)}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : undefined}
                >
                  {loading ? 'Loading…' : 'Load more'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Container>
    </>
  )
}
